import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 2px 0;
  min-width: 280px;
  animation: ${({ $visible }) => ($visible ? fadeIn : fadeOut)} 0.2s ease forwards;
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1.5px solid #e0e0e0;
  border-top-color: #e97024;
  animation: ${spin} 0.8s linear infinite;
`;

const StepTextWrap = styled.div`
  flex: 1;
  min-width: 0;
`;

const StepText = styled.span`
  display: block;
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;
  color: #666;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: ${({ $phase }) => ($phase === 'exit' ? 0 : 1)};
  transform: translateY(${({ $phase }) => ($phase === 'exit' ? '-4px' : '0')});
  transition: opacity 0.25s ease, transform 0.25s ease;
`;

const ElapsedTime = styled.span`
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  color: #999;
  white-space: nowrap;
  flex-shrink: 0;
`;

const formatElapsed = (totalSeconds) => {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
};

const ResearchProgressIndicator = ({
  isProcessing,
  progressMessage = 'Analyzing your question…',
}) => {
  const [phase, setPhase] = useState('enter');
  const [displayMessage, setDisplayMessage] = useState(
    progressMessage || 'Analyzing your question…'
  );
  const [visible, setVisible] = useState(isProcessing);
  const [elapsedSec, setElapsedSec] = useState(0);

  useEffect(() => {
    if (isProcessing) {
      setVisible(true);
    } else {
      const timer = setTimeout(() => setVisible(false), 200);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isProcessing]);

  useEffect(() => {
    if (!isProcessing) return undefined;
    if (progressMessage === displayMessage) return undefined;

    setPhase('exit');
    const timer = setTimeout(() => {
      setDisplayMessage(progressMessage);
      setPhase('enter');
    }, 320);
    return () => clearTimeout(timer);
  }, [progressMessage, displayMessage, isProcessing]);

  useEffect(() => {
    if (!isProcessing) {
      setElapsedSec(0);
      return undefined;
    }
    const interval = setInterval(() => {
      setElapsedSec((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isProcessing]);

  if (!visible) return null;

  return (
    <Wrapper $visible={isProcessing} role="status" aria-live="polite" aria-label="Research in progress">
      <Spinner aria-hidden="true" />
      <StepTextWrap>
        <StepText key={displayMessage} $phase={phase}>
          {displayMessage}
        </StepText>
      </StepTextWrap>
      <ElapsedTime aria-hidden="true">({formatElapsed(elapsedSec)})</ElapsedTime>
    </Wrapper>
  );
};

export default ResearchProgressIndicator;
